import { toJS } from 'mobx';

import googleClassroomManager from '../managers/GoogleClassroomManager';

import { registerClass } from '../SatCoreRegistry';

export default class GoogleClassroomService {
  static hasGoogleClassroomAuthorization = async () => {
    const hasAuthorization = await googleClassroomManager.checkGoogleClassroomAuthorization();
    return hasAuthorization;
  }

  static openGoogleClassroomAuthorizerWindow = async ({ newWindowFeaturesObj } = {}) => {
    const googleClassroomAuthUrl = await googleClassroomManager.fetchGoogleClassroomAuthUrl();

    const newWindowWidth = newWindowFeaturesObj?.width || '500';
    const newWindowHeight = newWindowFeaturesObj?.height || '500';
    const newWindowTop = newWindowFeaturesObj?.top || '500';
    const newWindowLeft = newWindowFeaturesObj?.left || '500';

    const newWindowFeatures = [
      `width=${newWindowWidth}`,
      `height=${newWindowHeight}`,
      `top=${newWindowTop}`,
      `left=${newWindowLeft}`
    ].join();

    window.open(googleClassroomAuthUrl, '_blank', newWindowFeatures);
  }

  static fetchGoogleClassroomsMap = async ({ store = true } = {}) => {
    const googleClassroomsArray = await googleClassroomManager.fetchGoogleClassrooms();
    if (Array.isArray(googleClassroomsArray)) {
      const googleClassroomsMap = new Map(googleClassroomsArray.map((googleClassroom) => {
        return [googleClassroom.id, googleClassroom];
      }));
      if (store) {
        googleClassroomManager.setGoogleClassroomsMap(googleClassroomsMap);
        return googleClassroomsMap;
      } else {
        return googleClassroomsMap;
      }
    }
  }

  static importGoogleClassrooms = async ({ googleClassroomsArray } = {}) => {
    const googleClassroomIds = Array.isArray(googleClassroomsArray) ? googleClassroomsArray : (
      Array.from(toJS(googleClassroomManager.selectedGoogleClassroomOptionsMap).values()) || []
    ).map((googleClassroom) => googleClassroom?.id).filter((googleClassroomId) => !!googleClassroomId);

    if (!googleClassroomIds?.length) {
      console.error('importGoogleClassroom: googleClassroomIds is required');
      return;
    }

    const response = await googleClassroomManager.importGoogleClassrooms({ googleClassroomIds });
    return response;
  }

  static connectGoogleToClassroom = async ({ classroom, googleClassroom } = {}) => {
    if (!classroom?.id || !googleClassroom?.id) {
      console.error('connectGoogleToClassroom: classroom.id and googleClassroom.id are required');
      return;
    }
    const response = await googleClassroomManager.connectGoogleToClassroom({
      classroomId: classroom.id,
      googleClassroomId: googleClassroom.id
    });
    return response;
  };

  static disconnectGoogleFromClassroom = async ({ classroom } = {}) => {
    if (!classroom?.id) {
      console.error('disconnectGoogleFromClassroom: classroom.id is required');
      return;
    }
    const response = await googleClassroomManager.disconnectGoogleFromClassroom({
      classroomId: classroom.id
    });
    return response;
  }

  static fetchGoogleClassroomRosterMap = async ({ classroom, store = true } = {}) => {
    if (!classroom?.googleClassroomId) {
      console.error('fetchGoogleClassroomRosterMap: classroom.googleClassroomId is required');
      return;
    }
    const googleClassroomRosterArray = await googleClassroomManager.fetchGoogleClassroomRoster({
      googleClassroomId: classroom.googleClassroomId
    });

    if (Array.isArray(googleClassroomRosterArray)) {
      const googleClassroomRosterMap = new Map(googleClassroomRosterArray.map((googleClassroomUser) => {
        return [googleClassroomUser.id, googleClassroomUser];
      }));
      if (store) {
        googleClassroomManager.setGoogleClassroomRosterMap(googleClassroomRosterMap);
        return googleClassroomRosterMap;
      } else {
        return googleClassroomRosterMap;
      }
    }
  }

  static importGoogleClassroomRosterUsers = async ({ classroom, googleClassroomRosterUsersArray } = {}) => {
    const googleClassroomUserIds = Array.isArray(googleClassroomRosterUsersArray) ? googleClassroomRosterUsersArray : (
      Array.from(toJS(googleClassroomManager.selectedGoogleClassroomRosterOptionsMap).values()) || []
    ).map((googleClassroomUser) => googleClassroomUser?.id).filter((googleClassroomUserId) => !!googleClassroomUserId);

    if (!classroom?.id || !googleClassroomUserIds?.length) {
      console.error('importGoogleClassroomRosterUsers: classroom.id and googleClassroomUserIds are required');
      return;
    }

    const response = await googleClassroomManager.importGoogleClassroomRosterUsers({
      classroomId: classroom?.id,
      googleClassroomUserIds
    });
    return response;
  }

  static connectGoogleToUser = async ({ googleClassroomUser, user } = {}) => {
    if (!googleClassroomUser?.id || !user?.userId) {
      console.error('connectGoogleToUser: googleClassroomUser.id user.userId are required');
      return;
    }
    const response = await googleClassroomManager.connectGoogleToUser({
      googleClassroomUserId: googleClassroomUser.id,
      userId: user.userId,
    });
    return response;
  };

  static disconnectGoogleFromUser = async ({ user } = {}) => {
    if (!user?.userId) {
      console.error('disconnectGoogleFromUser: user.userId is required');
      return;
    }
    const response = await googleClassroomManager.disconnectGoogleFromUser({
      userId: user.userId
    });
    return response;
  }
}

registerClass('GoogleClassroomService', GoogleClassroomService);
